import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminMerchantsGetAdminMerchantsIdGuidQueryKey,
  getAdminMerchantsGetAdminMerchantsQueryKey,
} from '@/lib/api/admin-merchants/admin-merchants';
import {
  getAdminStoresGetAdminMerchantsMerchantIdGuidStoresQueryKey,
  useAdminStoresPostAdminMerchantsMerchantIdGuidStores,
} from '@/lib/api/admin-stores/admin-stores';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  storeId: z.string().min(1, 'Store ID is required.').max(64),
  name: z.string().min(1, 'Name is required.').max(200),
  shopperStatement: z.string().min(1, 'Shopper statement is required.').max(140),
});
type Form = z.infer<typeof Schema>;

interface Props {
  merchantId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function AdminCreateStoreDialog({ merchantId, onOpenChange }: Props) {
  const open = merchantId !== null;
  const queryClient = useQueryClient();

  const mutation = useAdminStoresPostAdminMerchantsMerchantIdGuidStores({
    mutation: {
      onSuccess: async () => {
        if (merchantId) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getAdminStoresGetAdminMerchantsMerchantIdGuidStoresQueryKey(merchantId),
            }),
            queryClient.invalidateQueries({
              queryKey: getAdminMerchantsGetAdminMerchantsIdGuidQueryKey(merchantId),
            }),
          ]);
        }
        await queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsQueryKey() });
        toast.success('Store created.');
        reset();
        onOpenChange(false);
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { storeId: '', name: '', shopperStatement: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (!merchantId) return;
    try {
      await mutation.mutateAsync({ merchantId, data: values });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(status === 409 ? 'A store with that id already exists under this merchant.' : 'Could not create store.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onOpenChange(false))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add store</DialogTitle>
          <DialogDescription>
            Shopper statement appears on cardholder statements — Adyen caps it at 140 chars.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="create-store-id">Store ID</Label>
            <Input id="create-store-id" placeholder="store_001" autoComplete="off" {...register('storeId')} />
            {errors.storeId && <p className="text-xs text-destructive">{errors.storeId.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-store-name">Name</Label>
            <Input id="create-store-name" placeholder="Main Lot" {...register('name')} />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="create-store-statement">Shopper statement</Label>
            <Input id="create-store-statement" placeholder="DEALERWORKS SALES" {...register('shopperStatement')} />
            {errors.shopperStatement && <p className="text-xs text-destructive">{errors.shopperStatement.message}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending ? 'Creating…' : 'Create store'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
